/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueProfileGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Queue Profiles module.
----------------------------------------------------------------*/

import { Phone } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { QueueProfileFormState } from '../queueProfiles/types';

interface QueueProfileGeneralSectionProps {
  formData: QueueProfileFormState;
  onFieldChange: <K extends keyof QueueProfileFormState>(field: K, value: QueueProfileFormState[K]) => void;
}

export function QueueProfileGeneralSection({
  formData,
  onFieldChange
}: QueueProfileGeneralSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('queueProfiles.form.sections.general')} icon={<Phone className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* Queue Profile Name */}
        <SimpleTooltip content={t('queueProfiles.form.fields.queueProfileName.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="queueProfileName" className="text-left">
            {t('queueProfiles.form.fields.queueProfileName.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="queueProfileName"
            value={formData.queueProfileName}
            onChange={(event) => onFieldChange('queueProfileName', event.target.value)}
            placeholder={t('queueProfiles.form.fields.queueProfileName.placeholder')}
            required
          />
        </div>

        {/* Member Delay */}
        <SimpleTooltip content={t('queueProfiles.form.fields.memberDelay.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="memberDelay" className="text-left">
            {t('queueProfiles.form.fields.memberDelay.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="memberDelay"
            type="number"
            min="0"
            value={formData.memberDelay}
            onChange={(event) => onFieldChange('memberDelay', event.target.value)}
            placeholder={t('queueProfiles.form.fields.memberDelay.placeholder')}
          />
        </div>

        {/* Member Timeout */}
        <SimpleTooltip content={t('queueProfiles.form.fields.memberTimeout.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="memberTimeout" className="text-left">
            {t('queueProfiles.form.fields.memberTimeout.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="memberTimeout"
            type="number"
            min="1"
            value={formData.memberTimeout}
            onChange={(event) => onFieldChange('memberTimeout', event.target.value)}
            placeholder={t('queueProfiles.form.fields.memberTimeout.placeholder')}
          />
        </div>

        {/* Truncate Agents on Load */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.truncateAgentsOnLoad.tooltip')}>
            <Label htmlFor="truncateAgentsOnLoad" className="text-left">
              {t('queueProfiles.form.fields.truncateAgentsOnLoad.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="truncateAgentsOnLoad"
            value={formData.truncateAgentsOnLoad}
            onChange={(value) => onFieldChange('truncateAgentsOnLoad', value)}
          />
        </div>

        {/* Log Debug Info */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.logDebugInfo.tooltip')}>
            <Label htmlFor="logDebugInfo" className="text-left">
              {t('queueProfiles.form.fields.logDebugInfo.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="logDebugInfo"
            value={formData.logDebugInfo}
            onChange={(value) => onFieldChange('logDebugInfo', value)}
          />
        </div>

        {/* Strict Retry */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.strictRetry.tooltip')}>
            <Label htmlFor="strictRetry" className="text-left">
              {t('queueProfiles.form.fields.strictRetry.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="strictRetry"
            value={formData.strictRetry}
            onChange={(value) => onFieldChange('strictRetry', value)}
          />
        </div>

        {/* Skip Agents With Active Call */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.skipAgentsWithActiveCall.tooltip')}>
            <Label htmlFor="skipAgentsWithActiveCall" className="text-left">
              {t('queueProfiles.form.fields.skipAgentsWithActiveCall.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="skipAgentsWithActiveCall"
            value={formData.skipAgentsWithActiveCall}
            onChange={(value) => onFieldChange('skipAgentsWithActiveCall', value)}
          />
        </div>

        {/* Agent Cooldown Time */}
        <SimpleTooltip content={t('queueProfiles.form.fields.agentCooldownTime.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="agentCooldownTime" className="text-left">
            {t('queueProfiles.form.fields.agentCooldownTime.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="agentCooldownTime"
            type="number"
            min="0"
            value={formData.agentCooldownTime}
            onChange={(event) => onFieldChange('agentCooldownTime', event.target.value)}
            placeholder={t('queueProfiles.form.fields.agentCooldownTime.placeholder')}
          />
        </div>

        {/* Agent Busy Delay */}
        <SimpleTooltip content={t('queueProfiles.form.fields.agentBusyDelay.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="agentBusyDelay" className="text-left">
            {t('queueProfiles.form.fields.agentBusyDelay.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="agentBusyDelay"
            type="number"
            min="0"
            value={formData.agentBusyDelay}
            onChange={(event) => onFieldChange('agentBusyDelay', event.target.value)}
            placeholder={t('queueProfiles.form.fields.agentBusyDelay.placeholder')}
          />
        </div>

        {/* Agent Reject Delay */}
        <SimpleTooltip content={t('queueProfiles.form.fields.agentRejectDelay.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="agentRejectDelay" className="text-left">
            {t('queueProfiles.form.fields.agentRejectDelay.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="agentRejectDelay"
            type="number"
            min="0"
            value={formData.agentRejectDelay}
            onChange={(event) => onFieldChange('agentRejectDelay', event.target.value)}
            placeholder={t('queueProfiles.form.fields.agentRejectDelay.placeholder')}
          />
        </div>

        {/* Max Wait Size */}
        <SimpleTooltip content={t('queueProfiles.form.fields.maxWaitSize.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="maxWaitSize" className="text-left">
            {t('queueProfiles.form.fields.maxWaitSize.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="maxWaitSize"
            type="number"
            min="0"
            value={formData.maxWaitSize}
            onChange={(event) => onFieldChange('maxWaitSize', event.target.value)}
            placeholder={t('queueProfiles.form.fields.maxWaitSize.placeholder')}
          />
        </div>

        {/* Ring Progressively */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.ringProgressively.tooltip')}>
            <Label htmlFor="ringProgressively" className="text-left">
              {t('queueProfiles.form.fields.ringProgressively.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="ringProgressively"
            value={formData.ringProgressively}
            onChange={(value) => onFieldChange('ringProgressively', value)}
          />
        </div>

        {/* Skip Caller ID Number */}
        <SimpleTooltip content={t('queueProfiles.form.fields.skipCallerIdNumber.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="skipCallerIdNumber" className="text-left">
            {t('queueProfiles.form.fields.skipCallerIdNumber.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="skipCallerIdNumber"
            value={formData.skipCallerIdNumber}
            onChange={(event) => onFieldChange('skipCallerIdNumber', event.target.value)}
            placeholder={t('queueProfiles.form.fields.skipCallerIdNumber.placeholder')}
          />
        </div>
      </div>
    </FormBox>
  );
}

